// Controles del editor de marca de agua — mismo contrato y lenguaje visual que
// ImageCropControls: `variant="desktop"` llena la tarjeta lateral (título +
// controles + acciones al pie), `variant="mobile"` reemplaza la barra de
// acciones inferior de la superficie. Sin botón Cancelar: el volver/cerrar de
// cada superficie cierra el editor (mismo criterio que Recortar en mobile).

import { createPortal } from 'react-dom';
import { Check, Loader2, Plus, Save, Trash2 } from 'lucide-react';
import { TextToolbar } from '../../pages/edicionGrafica/panels/TextToolbar';
import type { WatermarkEditorState, WatermarkTab } from './useWatermarkEditor';
import { LogoControls } from './LogoControls';
import { OpacitySlider } from './OpacitySlider';

export const WatermarkEditorControls: React.FC<{
  state: WatermarkEditorState;
  variant: 'mobile' | 'desktop';
  /** Sobre el vidrio oscuro del visor del teléfono, como las herramientas de Editar
   *  (ADR-2026-10-28-la-marca-de-agua-en-oscuro). Sin la prop, claro, como siempre. */
  oscuro?: boolean;
}> = ({ state, variant, oscuro = false }) => {
  const {
    api,
    brandColors,
    config,
    ready,
    hadConfig,
    processing,
    logoError,
    saving,
    saveError,
    showSaved,
    brandLogoUrl,
    logoLayerExists,
    textLayerExists,
    activeTab,
    selectTab,
    addLogoLayer,
    addTextLayer,
    removeLayer,
    setLogoMode,
    handleSave,
    handleDisable,
    closeSaved,
  } = state;

  const segmented = (
    <div className={`h-9 rounded-md p-1 grid grid-cols-2 ${oscuro ? 'bg-white/[0.08]' : 'bg-neutral-100'}`}>
      {(['logo', 'texto'] as WatermarkTab[]).map((tab) => (
        <button
          key={tab}
          type="button"
          onClick={() => selectTab(tab)}
          className={`rounded-sm flex items-center justify-center text-[13px] transition ${
            activeTab === tab
              ? 'bg-white shadow-sm font-semibold text-brand-black'
              : `font-medium ${oscuro ? 'text-white/70' : 'text-neutral-600'}`
          }`}
        >
          {tab === 'logo' ? 'Logo' : 'Texto'}
        </button>
      ))}
    </div>
  );

  const botonSecundario = oscuro
    ? 'border border-white/[0.08] bg-white/[0.08] text-white/85'
    : 'border border-neutral-200 bg-surface-card text-neutral-600 hover:bg-surface-elevated';

  const addButton = (label: string, onClick: () => void) => (
    <button
      type="button"
      onClick={onClick}
      className={`w-full h-10 rounded-md flex items-center justify-center gap-1.5 text-[13px] font-medium transition-colors ${botonSecundario}`}
    >
      <Plus className="w-3.5 h-3.5" />
      {label}
    </button>
  );

  // Mismo estilo contorneado que el resto de los botones (nada de rojo: quitar
  // una capa es reversible — se re-agrega con "Agregar…").
  const removeButton = (tab: WatermarkTab, label: string) => (
    <button
      type="button"
      onClick={() => removeLayer(tab)}
      className={`w-full h-10 rounded-md flex items-center justify-center gap-1.5 text-[13px] font-medium transition-colors ${botonSecundario}`}
    >
      <Trash2 className="w-3.5 h-3.5" />
      {label}
    </button>
  );

  const tabContent =
    ready && config ? (
      activeTab === 'logo' ? (
        !brandLogoUrl ? (
          <p className={`text-[13px] ${oscuro ? 'text-white/55' : 'text-neutral-500'}`}>
            Subí tu logo en Configuración → Mis marcas para usarlo acá.
          </p>
        ) : logoLayerExists ? (
          <div className="flex flex-col gap-3">
            <LogoControls
              api={api}
              mode={config.logo.mode}
              processing={processing}
              error={logoError}
              onSetMode={setLogoMode}
              oscuro={oscuro}
            />
            {removeButton('logo', 'Eliminar logo')}
          </div>
        ) : (
          addButton('Agregar logo', addLogoLayer)
        )
      ) : textLayerExists ? (
        <div className="flex flex-col gap-3">
          {/* La barra de texto es la del editor gráfico (clara): en oscuro queda en su cajita
              blanca. */}
          <div className={`h-12 rounded-md overflow-hidden ${oscuro ? 'bg-white' : 'border border-neutral-200 bg-surface-card'}`}>
            <TextToolbar api={api} brandColors={brandColors} />
          </div>
          <OpacitySlider
            value={api.scene.layers.find((l) => l.id === 'wm-text')?.opacity ?? 1}
            onChange={(v) => api.updateLayer('wm-text', { opacity: v })}
            onCommit={() => api.checkpoint()}
            oscuro={oscuro}
          />
          {removeButton('texto', 'Eliminar texto')}
        </div>
      ) : (
        addButton('Agregar texto', addTextLayer)
      )
    ) : (
      <div className="flex items-center justify-center py-6">
        <Loader2 className="w-5 h-5 animate-spin text-brand-primary" />
      </div>
    );

  const saveButton = (
    <button
      type="button"
      onClick={handleSave}
      disabled={saving || !ready}
      className="w-full flex items-center justify-center gap-2 h-11 rounded-md text-[15px] font-medium bg-brand-primary text-white shadow-sm transition-colors hover:bg-brand-primary-hover disabled:opacity-40"
    >
      {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
      Guardar configuración
    </button>
  );

  // "Desactivar" solo existe cuando ya hay una marca configurada, y en estilo
  // secundario neutro (no es destructivo: la config queda guardada, apagada).
  const disableButton = hadConfig ? (
    <button
      type="button"
      onClick={handleDisable}
      disabled={saving}
      className="h-11 px-4 rounded-md border border-neutral-200 bg-surface-card text-neutral-600 hover:bg-surface-elevated text-[15px] font-medium transition-colors shrink-0"
    >
      Desactivar
    </button>
  ) : null;

  // Confirmación post-guardado (portal: cubre toda la app, como los modales).
  const savedModal =
    showSaved &&
    createPortal(
      <div className="fixed inset-0 z-[230] bg-brand-black/50 backdrop-blur-sm flex items-center justify-center p-6">
        <div className="w-full max-w-[340px] bg-white rounded-lg shadow-lg p-5 flex flex-col items-center text-center gap-2.5">
          <div className="w-11 h-11 rounded-full bg-success-soft flex items-center justify-center text-success-text">
            <Check className="w-5 h-5" strokeWidth={2.5} />
          </div>
          <p className="text-[16px] font-semibold text-brand-black">Marca de agua guardada</p>
          <p className="text-[13px] text-neutral-600 leading-snug">
            Al descargar tus imágenes ahora vas a poder elegir si las querés con o sin la marca de
            agua.
          </p>
          <button
            type="button"
            onClick={closeSaved}
            className="w-full h-10 mt-1 rounded-md bg-brand-primary hover:bg-brand-primary-hover text-white text-[13px] font-medium transition-colors"
          >
            Entendido
          </button>
        </div>
      </div>,
      document.body,
    );

  if (variant === 'mobile') {
    // Sin Cancelar ni Desactivar acá: el volver de la superficie cancela y el
    // "Desactivar" flota arriba a la derecha del lienzo (WatermarkEditorCanvas).
    return (
      <div className="space-y-3">
        {segmented}
        {tabContent}
        {saveError && <p className="text-[12px] text-error">{saveError}</p>}
        <div className="pt-1">{saveButton}</div>
        {savedModal}
      </div>
    );
  }

  // Desktop: llena la tarjeta lateral (mismo esqueleto que Recortar).
  return (
    <div className="flex flex-col h-full">
      <div className="flex-1 flex flex-col gap-4 p-4 overflow-y-auto">
        <div>
          <h3 className="text-[17px] font-semibold text-brand-black">Marca de agua</h3>
          <p className="text-[13px] text-neutral-500 mt-1">
            Configurá el logo y el texto para tus descargas
          </p>
        </div>
        {segmented}
        {tabContent}
        {saveError && <p className="text-[12px] text-error">{saveError}</p>}
      </div>
      <div className="flex-shrink-0 p-4 pt-3 border-t border-neutral-200 flex gap-2">
        {disableButton}
        <div className="flex-1 min-w-0">{saveButton}</div>
      </div>
      {savedModal}
    </div>
  );
};
